/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Pagination from "../../components/Pagination";
import { getPermissions } from "../../context/AuthContext";
import Spinner from "../../theme/Spinner";
import { Autocomplete, TextField } from "@mui/material";
import {
  getAllPackingTypesApi,
  packingTypesListApi,
  updatePackingTypeStatusApi,
} from "../../services/_packingTypes";

const PackingTypeList = () => {
  const permissions = getPermissions();
  const [records, setRecords] = useState([]);
  const [packingTypeNameList, setPackingTypeNameList] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [applyFilter, setApplyFilter] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>("");
  const [displayFilter, setDisplayFilter] = useState(false);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortType, setSortType] = useState<string>("");

  const toggleFilter = async () => {
    setDisplayFilter(!displayFilter);
  };

  const fetchNameSuggestions = async () => {
    const result: any = await getAllPackingTypesApi();
    if (result.isSuccess) {
      setPackingTypeNameList(result?.data || []);
    }
  };

  const getPackingTypeList = async (
    page: number,
    perPage: number,
    isFilter?: boolean,
    sortKey?: string,
    sortType?: string
  ) => {
    setLoading(true);
    let query = `page=${page}&per_page=${perPage}`;
    if (isFilter) {
      if (filterName) {
        query += `&name=${filterName}`;
      }
      if (sortKey && sortType) {
        query += `&sort_key=${sortKey}&sort_type=${sortType}`;
      }
    }
    const result: any = await packingTypesListApi(query);
    if (result.isSuccess) {
      const totalPagesCount = Math.ceil(result.data.total_records / perPage);
      setTotalRecords(result.data.total_records);
      setTotalPages(totalPagesCount);
      setRecords(result.data.records);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNameSuggestions();
  }, []);

  const handleFilter = async () => {
    getPackingTypeList(page, perPage, true);
  };

  const resetList = async () => {
    setPage(1);
    setApplyFilter(false);
    setFilterName("");
    getPackingTypeList(page, perPage);
  };

  const prevButton = async () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const nextButton = async () => {
    if (totalPages > page) {
      setPage(page + 1);
    }
  };

  const updatePackingTypeStatus = async (id: string, status: boolean) => {
    setLoading(true);
    await updatePackingTypeStatusApi({
      id: id,
      status: !status,
    });
    getPackingTypeList(page, perPage, true);
    setLoading(false);
  };

  const sortTable = async (key: string) => {
    let type = sortType === "asc" ? "desc" : "asc";
    if (sortKey !== key) {
      type = "asc";
    }
    setSortKey(key);
    setSortType(type);
    await getPackingTypeList(page, perPage, true, key, type);
  };

  useEffect(() => {
    if (page && perPage) {
      getPackingTypeList(page, perPage, applyFilter);
    }
  }, [page, perPage, applyFilter]);

  return (
    <>
      <div className="card">
        <div className="card-header pb-0 d-flex justify-content-between">
          <div>
            <h5>PackingType List</h5>
          </div>

          <div>
            <Link
              to="#"
              type="button"
              className="btn btn-label-github"
              style={{ marginRight: "15px" }}
              onClick={toggleFilter}
            >
              <span>
                <i className="ti ti-filter me-sm-1"></i>
                <span className="d-none d-sm-inline-block">Filter</span>
              </span>
            </Link>
            {permissions.includes("create-packing-type") && (
              <Link
                className="btn btn-primary waves-effect waves-light"
                type="button"
                to="/create-packing-type"
              >
                <span>
                  <i className="ti ti-plus me-sm-1"></i>
                  <span className="d-none d-sm-inline-block">
                    Add PackingType
                  </span>
                </span>
              </Link>
            )}
          </div>
        </div>

        {displayFilter && (
          <div className="card-body">
            <form className="dt_adv_search">
              <div className="row">
                <div className="col-12">
                  <div className="row g-3">
                    <div className="col-12 col-sm-6 col-lg-3">
                      <label
                        className="form-label"
                        style={{ fontSize: "0.9375rem" }}
                      >
                        Name:
                      </label>
                      <Autocomplete
                        disablePortal
                        options={packingTypeNameList.map(
                          (suggestion: any) => suggestion.name
                        )}
                        onChange={(event, newValue) => setFilterName(newValue)}
                        value={filterName}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className="form-control"
                            type="text"
                            placeholder="Enter Name"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                          />
                        )}
                      />
                    </div>
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleFilter}
                      >
                        Filter
                      </button>
                      <button
                        type="button"
                        className="btn btn-label-github ms-5"
                        onClick={resetList}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="card-datatable table-responsive">
          {loading && <Spinner isFullScreen={false} />}
          <table className="dt-advanced-search table table-nowrap">
            <thead>
              <tr>
                <th>#</th>
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("name")}
                >
                  Name
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("weight")}
                >
                  Weight
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("description")}
                >
                  Description
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                {permissions.includes("update-packing-type") && (
                  <th
                    className="cursor-pointer"
                    onClick={() => sortTable("status")}
                  >
                    Status
                    <span>
                      <i className="ti ti-arrows-down-up sorting-icon"></i>
                    </span>
                  </th>
                )}
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("created_at")}
                >
                  Created At
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                {permissions.includes("update-packing-type") && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((item: any, i: number) => (
                  <tr key={item.id}>
                    <td>{(page - 1) * perPage + 1 + i}</td>
                    <td>{item.name}</td>
                    <td>{item.weight}</td>
                    <td>{item.description}</td>
                    {permissions.includes("update-packing-type") && (
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="status"
                            defaultChecked={item.status ? true : false}
                            onClick={() => {
                              updatePackingTypeStatus(item.id, item.status);
                            }}
                          />
                        </div>
                      </td>
                    )}
                    <td>
                      {moment(item.created_at).format("YYYY-MM-DD HH:mm:ss")}
                    </td>
                    {permissions.includes("update-packing-type") && (
                      <td>
                        <>
                          <Link
                            to={`/edit-packing-type/${item.id}`}
                            className="btn btn-sm btn-text-secondary rounded-pill btn-icon"
                            title="Edit PackingType"
                          >
                            <i className="ti ti-pencil ti-md"></i>
                          </Link>
                        </>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={
                      permissions.includes("update-packing-type") ? 8 : 6
                    }
                    className="text-center"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          perPage={perPage}
          totalRecords={totalRecords}
          totalPages={totalPages}
          records={records}
          prevButton={prevButton}
          nextButton={nextButton}
        />
      </div>
    </>
  );
};

export default PackingTypeList;
